import el from "@cypherpotato/el";
import { Button, ButtonRow, PrimaryButton } from "../../../components/button";
import { Card, CardNavigation } from "../../../components/card";
import { CodeEditor } from "../../../components/code-editor";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { Editor, EditorDescription, EditorTitle } from "../../../components/editor";
import { TextArea } from "../../../components/form";
import { Icon } from "../../../components/icon";
import { JsonControl } from "../../../components/json-control";
import { ModelPickerControl } from "../../../components/model-picker";
import { Playground, PlaygroundAside, PlaygroundAsideContainer, PlaygroundContent } from "../../../components/playground";
import { HorizontalSizableContainer, HorizontalSizableSection, SizableContainer, SizableSection } from "../../../components/sizable-container";
import { TabControl } from "../../../components/tabcontrol";
import { displayFormControl } from "../../../ui/alert";
import { runOperation } from "../../../ui/operation";
import { find, valueFrom } from "../../../util";
import { copyToClipboard } from "../../../ui/clipboard";
import { api } from "../../../api";
import { Dropdown, DropdownDivider, DropdownItem } from "../../../components/dropdown";
import { DataPickerControl } from "../../../components/data-picker";
import { MutedText } from "../../../components/text";
import InputListBox from "../../../components/input-list-box";
import { openStorage } from "../../../storage";
import { setManagedInterval } from "../../../interval-manager";
import { requestAiEdit } from "../../../ui/ai-edit";
import { acceptDiff } from "../../../ui/accept-diff";
import { Constants } from "../../../constants";

export default function SysinsBuilder() {

    const text = el.state.create("");

    setManagedInterval(() => {

        // auto save draft
        openStorage("sysins-builder").then(async storage => {
            const currentText = valueFrom("contents");
            if (currentText.length)
                await storage.set("[auto-save]", currentText);
        }, 10000);
    })

    function defaultParameters() {
        return {
            model: valueFrom("modelName"),
            reasoningEffort: valueFrom("reasoningEffort"),
        }
    }

    function generateInstructions() {
        const whatItShouldDo = valueFrom("whatShouldItDo");
        const whatItShouldNotDo = valueFrom("whatShouldNotDo");
        const expectedResponseExamples = valueFrom("responseExamples");
        const generationFormat = valueFrom("generationFormat");
        const currentInstructions = valueFrom("contents");

        runOperation("Generating system instructions", async () => {
            const resp = await api.postJson("/api/v2/helper-tools/system-prompt", {
                ...defaultParameters(),
                input: {
                    whatItShouldDo,
                    whatItShouldNotDo,
                    expectedResponseExamples,
                    generationFormat,
                    currentText: currentInstructions
                }
            });

            text.value = resp.data.systemInstructions;
        });
    }

    function extractFields() {
        runOperation("Extracting fields from current instructions...", async () => {
            const resp = await api.postJson("/api/v2/helper-tools/extract-system-prompt", {
                ...defaultParameters(),
                input: {
                    instructions: valueFrom("contents")
                }
            });

            find("whatShouldItDo").value = resp.data.positivePrompt || "";
            find("whatShouldNotDo").value = resp.data.negativePrompt || "";
            find("responseExamples").value = resp.data.expectedExamples || "";
        });
    }

    return Dashboard(
        {
            classList: ["full-size"]
        },
        Card(
            CardNavigation(
                CommandBar(
                    ButtonRow(
                        PrimaryButton(
                            Icon("ai-generate-2-fill"),
                            "Generate",
                            {
                                onClick() {
                                    generateInstructions();
                                }
                            }
                        ),
                        Button(
                            Icon("ai-generate-2-line"),
                            "Clear and generate",
                            {
                                onClick() {
                                    find("contents").value = "";
                                    generateInstructions();
                                }
                            }
                        )
                    ),
                    ButtonRow(
                        Button(
                            Icon("file-copy-line"),
                            "Copy to clipboard",
                            {
                                onClick() {
                                    const contents = valueFrom("contents");
                                    copyToClipboard(contents);
                                }
                            }
                        )
                    ),
                    Button(
                        Icon("quill-pen-ai-line"), "Edit with AI",
                        {
                            onClick() {
                                requestAiEdit({
                                    inputData: {
                                        file: "instructions.md",
                                        fileContents: valueFrom("contents")
                                    },
                                    subject: valueFrom("generationFormat"),
                                    description: "Edit the instructions with AI"
                                }).then(result => {
                                    acceptDiff(find("contents").value, result).then((result) => {
                                        find("contents").value = result;
                                    });
                                });
                            }
                        }
                    ),
                    ButtonRow(
                        Button(
                            Icon("save-line"),
                            "Save",
                            {
                                async onClick() {
                                    const storage = await openStorage("sysins-builder");
                                    const items = (await storage.list())
                                        .map(i => ({ value: i.key, text: i.key }));

                                    displayFormControl({
                                        title: "Save as template",
                                        message: "Choose a name for the system instructions template you want to save.",
                                        formControlItem: InputListBox({
                                            items: items,
                                            placeholder: "Instructions name",
                                            async onRemove(item) {
                                                await storage.remove(item.value);
                                                return true;
                                            }
                                        }),
                                        validate: [
                                            [x => x.length, "You must provide a name for the template."]
                                        ],
                                        async onConfirm(value) {
                                            await storage.set(value, valueFrom("contents"));
                                        }
                                    })
                                }
                            }
                        ),
                        Button(
                            Icon("folder-open-line"),
                            "Load",
                            {
                                async onClick() {
                                    const storage = await openStorage("sysins-builder");
                                    const items = (await storage.list())
                                        .map(i => ({ value: i.key, text: i.key }));

                                    displayFormControl({
                                        title: "Load template",
                                        message: "Choose a system instructions template to load.",
                                        formControlItem: DataPickerControl({
                                            data: items
                                        }),
                                        async onConfirm(value) {
                                            const content = await storage.get(value);
                                            text.value = content;
                                        }
                                    })
                                }
                            }
                        )
                    ),
                    ButtonRow(
                        Button(
                            Icon("menu-search-line"),
                            "Extract fields",
                            {
                                onClick() {
                                    extractFields();
                                }
                            }
                        )
                    )
                )
            ),
            Playground(
                HorizontalSizableContainer(
                    HorizontalSizableSection(
                        {
                            initialSize: "400px"
                        },
                        PlaygroundAside(
                            TabControl([
                                {
                                    title: "Instructions",
                                    icon: Icon("quote-text"),
                                    contents: PlaygroundAsideContainer(
                                        SizableContainer(
                                            SizableSection("Parameters",
                                                Editor(
                                                    {
                                                        id: "modelName",
                                                        value: "@openai/gpt-5.1",
                                                        renderValue(value) {
                                                            return Button(value);
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: "Choose model",
                                                                message: "Choose the model you want to use in the AI function.",
                                                                dialogSize: "large",
                                                                formControlItem: ModelPickerControl({ selectedModel: value }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Model")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "reasoningEffort",
                                                        value: null,
                                                        renderValue(value) {
                                                            return Button(value || MutedText("Unset"));
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: this.editorTitle,
                                                                message: this.editorParagraphs,
                                                                formControlItem: DataPickerControl({
                                                                    data: Constants.NullableReasoningEffortSelectItems,
                                                                    value,
                                                                    allowNone: false
                                                                }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Reasoning effort")
                                                    )
                                                ),
                                                Editor(
                                                    {
                                                        id: "generationFormat",
                                                        value: "System instruction",
                                                        renderValue(value) {
                                                            return Button(value);
                                                        },
                                                        manipulateValue(value, callback) {
                                                            displayFormControl({
                                                                title: "Generation format",
                                                                message: "Choose whether the model should generate system instructions or user prompts.",
                                                                formControlItem: DataPickerControl({
                                                                    value,
                                                                    data: [
                                                                        { value: "System instruction", text: "System instruction" },
                                                                        { value: "User prompt", text: "User prompt" },
                                                                        { value: "Skill", text: "Skill" },
                                                                    ]
                                                                }),
                                                                onConfirm: callback,
                                                                validate: []
                                                            })
                                                        }
                                                    },
                                                    EditorDescription(
                                                        EditorTitle("Generation format")
                                                    )
                                                ),
                                            ),
                                            SizableSection(
                                                "Positive prompt",
                                                TextArea(null, null, {
                                                    id: "whatShouldItDo",
                                                    placeholder: "It should..."
                                                })
                                            ),
                                            SizableSection(
                                                "Negative prompt",
                                                TextArea(null, null, {
                                                    id: "whatShouldNotDo",
                                                    placeholder: "The instructions should not..."
                                                })
                                            ),
                                            SizableSection(
                                                "Response examples",
                                                TextArea(null, null, {
                                                    id: "responseExamples"
                                                })
                                            ),
                                        )
                                    )
                                },
                            ], {
                                type: "vertical"
                            })
                        )
                    ),
                    HorizontalSizableSection(
                        {
                            initialSize: "calc(100% - 400px)"
                        },
                        PlaygroundContent(
                            {
                                style: {
                                    "overflow": "hidden"
                                }
                            },
                            el.state.listening([text], () =>
                                CodeEditor({
                                    value: text.value,
                                    props: {
                                        id: "contents",
                                    },
                                    language: "markdown",
                                    wrapperProps: {
                                        style: {
                                            height: "100%",
                                            width: "100%",
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            border: "none",
                                            overflowY: "scroll"
                                        }
                                    }
                                })
                            )
                        )
                    )
                )
            )
        )
    )
}
